/**
 * What the ads connection is, in one word — derived once, for the card badge
 * and the tab mark alike.
 *
 * ## WHY THIS IS A MODULE AND NOT A TERNARY IN THE CARD
 *
 * `integration-tabs.tsx` spends several paragraphs on it: a rail that derived
 * its own verdict would put a *second* answer to "is this connection healthy"
 * a centimetre from the card's, and the two would disagree the moment somebody
 * pressed Test. So the panel is the only source and the rail draws what it is
 * handed — which only works if the panel has exactly one place it decides.
 *
 * ## THE ORDER IS THE DESIGN
 *
 * Several of these are true at once on a real connection: an account in its
 * grace period, with a read-only token, expiring next week, is all three. The
 * badge has room for one, so the order is **what blocks the most**, not what is
 * most recent:
 *
 *   1. the check failed          — nothing works at all
 *   2. Meta will not spend       — it connected, and no ad can run
 *   3. the grant is not known    — we cannot say whether a write is allowed
 *   4. it may not read at all    — nothing about the account can be shown
 *      it may read, not manage   — reporting works, nothing can be changed
 *   5. the token expires         — everything works, and stops working
 *
 * Reversing any pair hides the bigger problem behind the smaller one. In
 * particular 3 outranks 4: "not checked" and "not permitted" send somebody to
 * different places, and guessing the second when the truth is the first sends
 * them into Business Manager to fix something that was never wrong.
 *
 * ## `unknown` IS NEVER GREEN
 *
 * Meta's `account_status` maps to `unknown` for a code this product has no word
 * for. That is not a healthy account — it is an unread one — and the same rule
 * every figure on these screens follows applies: nothing known and working are
 * different statements and only one of them is ever true.
 */
import type { BadgeTone } from '@wend/ui';
/*
 * FROM `@wend/shared` AND NOT THROUGH `lib/api/meta-ads`, WHICH RE-EXPORTS THE
 * SAME THINGS.
 *
 * These two are *values*, so the import is not erased — and the client module
 * reaches `lib/config.ts` and its `import.meta.env`, which evaluates only
 * inside Vite. Importing them through the client makes this module, and every
 * suite that renders anything depending on it, unloadable outside a browser
 * build. It cost one run to find out.
 */
import {
  metaAdAccountCanSpend,
  metaTokenExpiry,
  type MetaAdsIntegration,
} from '@wend/shared';

/** How loudly the rail should mark the tab. */
export type MetaAdsAttention = 'none' | 'info' | 'warn' | 'bad';

export interface MetaAdsTabStatus {
  readonly label: string;
  readonly attention: MetaAdsAttention;
}

/**
 * Attention → the tone the `Badge` and the rail's mark both take.
 *
 * Exported and shared rather than each site choosing, because a badge reading
 * "Read only" in amber beside a tab dot in green is the screen disagreeing with
 * itself about the same fact.
 */
export const META_ADS_ATTENTION_TONE: Readonly<Record<MetaAdsAttention, BadgeTone>> = {
  none: 'good',
  info: 'neutral',
  warn: 'warn',
  bad: 'bad',
};

/** The word for what Meta said about the account, when it is not simply active. */
const ACCOUNT_STATUS_LABEL: Readonly<Record<string, string>> = {
  disabled: 'Account disabled',
  closed: 'Account closed',
  unsettled: 'Account unsettled',
  pending_review: 'Account in review',
  unknown: 'Account state unread',
};

/**
 * The one decision. `null` means this panel does not know yet, and nothing is
 * reported for it — a rail mark cleared by a request that never came back is
 * worse than no mark.
 */
export function metaAdsTabStatus(integration: MetaAdsIntegration | null): MetaAdsTabStatus {
  /*
   * A workspace that has not connected an ad account has nothing wrong with it.
   * `info` rather than `warn`, and the rail draws no mark for it at all — see
   * `STATUS_MARKS`, where `neutral` is deliberately `null`.
   */
  if (integration === null) return { label: 'Not connected', attention: 'info' };

  if (integration.status === 'failed') return { label: 'Check failed', attention: 'bad' };

  /*
   * 2. It connected and Meta will not spend through it. `accountStatus` is null
   *    only before the first successful check, which `status === 'failed'`
   *    above has already caught — so a null here is a row in a state this
   *    function does not have a word for, and it says so rather than passing.
   */
  if (integration.accountStatus === null) return { label: 'Not checked', attention: 'warn' };
  if (!metaAdAccountCanSpend(integration.accountStatus)) {
    return {
      label: ACCOUNT_STATUS_LABEL[integration.accountStatus] ?? 'Account state unread',
      attention: integration.accountStatus === 'unknown' ? 'warn' : 'bad',
    };
  }

  /* 3. Meta would not say what the token may do. Not the same as "may not". */
  if (!integration.grant.known) return { label: 'Permissions not checked', attention: 'warn' };

  /*
   * 4a. Meta answered, and the token may not read ads at all.
   *
   * Reachable, and it was missed in slice 40: a token carrying only
   * `business_management` can still resolve an ad account's name and currency,
   * so its check passes — and this function used to call it "Read only", which
   * is exactly the one thing it cannot do. Slice 41's read found it, because a
   * read is the first thing that asks `canRead` rather than `canManage`.
   */
  if (!integration.grant.canRead) return { label: 'No ads permission', attention: 'bad' };

  /* 4b. Reporting works; nothing can be created, started or paused. */
  if (!integration.grant.canManage) return { label: 'Read only', attention: 'warn' };

  /*
   * 5. Everything works and stops working on a date.
   *
   * `metaTokenExpiry` is `@wend/shared`'s, the same function the WhatsApp card
   * reads — and its `never` answer is the one that comes from `expiresAt: null`
   * on an INTROSPECTED token. An unintrospected token also has a null expiry
   * and is caught by rule 3 above, which is why this can trust the null here.
   */
  const expiry = metaTokenExpiry(integration.tokenExpiresAt);
  if (expiry === 'expiring') return { label: 'Token expiring', attention: 'warn' };
  if (expiry === 'expired') return { label: 'Token expired', attention: 'bad' };

  return { label: 'Connected', attention: 'none' };
}
